// src/components/WelcomeScreen.jsx
import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
// Import your video and audio files
import assets from '../assets/assets' // Add your video here

const WelcomeScreen = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [fadeOut, setFadeOut] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    const audio = audioRef.current;
    
    if (video) {
      // Mute video for autoplay support
      video.muted = true;
      video.loop = true;

      // Start playing when video can play
      const handleCanPlay = () => {
        video.play().catch(err => {
          console.log('Video autoplay prevented:', err);
        });
      };

      video.addEventListener('canplaythrough', handleCanPlay);

      // Setup audio
      if (audio) {
        audio.loop = true;
        audio.volume = 0.15; // REDUCED VOLUME to 15%
        
        // Try to play audio when user interacts
        const playAudio = () => {
          if (!audioPlaying) {
            audio.play()
              .then(() => {
                setAudioPlaying(true);
                console.log('Audio started playing at low volume');
              })
              .catch(err => {
                console.log('Audio autoplay prevented:', err);
                // Show a mute/unmute button if needed
              });
          }
        };

        // Try to play audio on component mount
        playAudio();

        // Also try to play audio on any user interaction
        const handleUserInteraction = () => {
          playAudio();
          // Remove event listeners after first interaction
          document.removeEventListener('click', handleUserInteraction);
          document.removeEventListener('keydown', handleUserInteraction);
          document.removeEventListener('touchstart', handleUserInteraction);
        };

        // Add event listeners for user interaction
        document.addEventListener('click', handleUserInteraction);
        document.addEventListener('keydown', handleUserInteraction);
        document.addEventListener('touchstart', handleUserInteraction);

        // Cleanup
        return () => {
          video.removeEventListener('canplaythrough', handleCanPlay);
          video.pause();
          audio.pause();
          document.removeEventListener('click', handleUserInteraction);
          document.removeEventListener('keydown', handleUserInteraction);
          document.removeEventListener('touchstart', handleUserInteraction);
        };
      }
    }
  }, []);

  // Handle mute/unmute functionality with lower volume
  const toggleAudio = () => {
    if (audioRef.current) {
      if (audioRef.current.volume > 0) {
        audioRef.current.volume = 0;
        // Optionally change button text or icon to muted
      } else {
        audioRef.current.volume = 0.15; // Set back to low volume (15%)
        // Optionally change button text or icon to unmuted
      }
    }
  };

  const handleNavigation = () => {
    setFadeOut(true);
    // Fade out audio smoothly
    if (audioRef.current) {
      const fadeOutAudio = () => {
        if (audioRef.current.volume > 0.01) {
          audioRef.current.volume -= 0.01;
          setTimeout(fadeOutAudio, 30);
        } else {
          audioRef.current.pause();
          audioRef.current.volume = 0.15; // Reset to low volume for next time
        }
      };
      fadeOutAudio();
    }
    setTimeout(() => {
      navigate('/home');
    }, 600);
  };

  return (
    <>
      {/* AUDIO ELEMENT - Hidden but playing in background at low volume */}
      <audio
        ref={audioRef}
        preload="auto"
        style={{ display: 'none' }}
      >
        <source src={assets.music} type="audio/mpeg" />
        <source src={assets.music} type="audio/ogg" />
        Your browser does not support the audio element.
      </audio>

      {/* Optional Audio Control Button with volume indicator */}
      {/* 
      <button 
        onClick={toggleAudio}
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 100,
          padding: '10px 15px',
          background: 'rgba(255,255,255,0.15)',
          border: '1px solid rgba(255,255,255,0.3)',
          borderRadius: '25px',
          color: 'white',
          cursor: 'pointer',
          backdropFilter: 'blur(10px)',
          fontSize: '0.9rem',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        {audioRef.current?.volume > 0 ? '🔊 Low' : '🔇 Muted'}
      </button>
      */}

      <div
        className="welcome-screen"
        style={{
          opacity: fadeOut ? 0 : 1,
          transition: "opacity 0.6s ease-out",
        }}
      >

        {/* VIDEO BACKGROUND */}
        <video
          ref={videoRef}
          className="welcome-video"
          playsInline
          autoPlay
          muted
          loop
          preload="auto"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            objectFit: 'cover',
            zIndex: -2,
          }}
        >
          {/* Multiple formats for better browser support */}
          <source src={assets.welcome} type="video/mp4" />
          {/* Fallback image if video fails to load */}
          <img src="../assets/welcome.jpg" loading="lazy" alt="Welcome Background" />
        </video>

        {/* DARK OVERLAY for better text readability */}
        <div
          className="video-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: -1,
          }}
        />

        {/* Floating Particles */}
        <div className="particles">
          {[...Array(8)].map((_, i) => (
            <span key={i} style={{ '--i': i }} />
          ))}
        </div>

        {/* Center Content */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
          style={{
            textAlign: 'center',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1.5rem',
            padding: '0 2rem',
          }}
        >
          {/* LOGO with SLOW ZOOM IN */}
          <motion.img
            src={assets.logo}
            loading="lazy"
            alt="Logo"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              duration: 2,
              ease: 'easeOut',
            }}
            style={{
              width: '25vw',
              maxWidth: '350px',
              minWidth: '250px',
              marginTop: '30px',
              marginLeft: '70px',
              height: 'auto',
              filter: `
                drop-shadow(0 0 20px rgba(255, 255, 255, 0.9))
                drop-shadow(0 0 40px rgba(255, 255, 255, 0.7))
                drop-shadow(0 0 60px rgba(255, 255, 255, 0.5))
                drop-shadow(0 10px 20px rgba(0, 0, 0, 0.4))
              `,
            }}
          />

          {/* BUTTON with SLOW ZOOM IN */}
          <motion.button
            onClick={handleNavigation}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              delay: 1,
              duration: 1.5,
              ease: 'easeOut',
            }}
            whileHover={{
              scale: 1.08,
              boxShadow: '0 20px 40px rgba(255, 255, 255, 0.3)',
            }}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: '16px 48px',
              fontSize: '1.3rem',
              fontWeight: 600,
              fontFamily: "'Raleway', sans-serif",
              background: 'rgba(255, 255, 255, 0.95)',
              color: '#040695',
              border: 'none',
              borderRadius: '50px',
              marginLeft: '70px',
              cursor: 'pointer',
              boxShadow: '0 15px 35px rgba(0,0,0,0.3)',
              letterSpacing: '1px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            Welcome
          </motion.button>
        </motion.div>
      </div>

      {/* ENHANCED STYLES */}
      <style jsx>{`
        .welcome-screen {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-family: "'Playfair Display', serif";
        }

       
        @keyframes float {
          0% {
            transform: translateY(100vh) translateX(0);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-100px) translateX(100px);
            opacity: 0;
          }
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .welcome-screen img {
            width: 40vw !important;
            max-width: 200px;
            min-width: 120px;
            margin-left: 0px !important;
          }
          
          .welcome-screen button {
            padding: 14px 36px !important;
            font-size: 1.1rem !important;
            margin-left: 0px !important;
          }
        }

        @media (max-width: 480px) {
          .welcome-screen img {
            width: 50vw !important;
            max-width: 180px;
            min-width: 100px;
          }
          
          .welcome-screen button {
            padding: 12px 32px !important;
            font-size: 1rem !important;
          }
        }

        /* Video Loading Animation */
        .welcome-video::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1;
          opacity: 1;
          transition: opacity 0.5s ease;
        }

        .welcome-video:not([src])::before,
        .welcome-video:not(:playing)::before {
          opacity: 1;
        }
      `}</style>
    </>
  );
};

export default WelcomeScreen;